import { assertNonNullish, NonNullish } from 'utils/nullish-check';
import { getRegionSwitchCountryData } from 'utils/shop-region-countries';

type Config = {
  /**
   * Whether the sites will be set from the `hreflang` elements in the head.
   * If false, you'll have to set the sites manually.
   */
  readonly setSitesFromHreflangs: boolean;
  /** Whether to redirect to the selected site if the user has already selected a site. Only works if the user has selected a site. */
  readonly redirectIfHasSelected: boolean;
  readonly sites?: Array<Site> | undefined;
};

type Site = {
  /** ISO 3166-1 alpha-2 */
  locale: string;
  /** ISO 3166-1 alpha-2 or "default" */
  region: string;
  url: string;
  isCurrent: boolean;
};

type RegionFilter = Partial<{
  region: string;
  locale: string;
}>;

const STORAGE_KEY = 'theme:selected-region';
const EXPLICIT_STORAGE_KEY = 'theme:selected-region-explicit';

type SelectSiteOptions = {
  explicit?: boolean;
};

type SaveSiteOptions = {
  explicit?: boolean;
};

export default class ShopRegions {
  private readonly config: Config;
  readonly sites: Array<Site> = [];

  constructor(config: Config) {
    this.config = config;

    if (this.config.sites) {
      this.sites.push(...this.config.sites);
    }

    if (config.setSitesFromHreflangs) {
      this.sites.push(...this.getSitesFromHreflangs());
    }

    if (this.sites.length === 0) {
      const countrySites = this.getSitesFromCountryData();
      this.sites.push(...(countrySites.length > 0 ? countrySites : [this.getRuntimeCurrentSite()]));
    }

    const url = new URL(window.location.href);
    if (url.searchParams.get('redirect')) {
      const site = this.getCurrentSite();
      const explicit = url.searchParams.get('region_selected') === '1';

      this.saveSiteToStorage(site, { explicit });
    }

    this.getRankedRegions = this.getRankedRegions.bind(this);
    this.getFilteredRegions = this.getFilteredRegions.bind(this);
    this.getIntendedSite = this.getIntendedSite.bind(this);
  }

  private normalizePathname(pathname: string): string {
    if (pathname === '/' || pathname === '') return '/';
    return pathname.replace(/\/+$/, '') || '/';
  }

  private sameMarketUrl(a: string, b: string): boolean {
    try {
      const ua = new URL(a);
      const ub = new URL(b);
      return (
        ua.origin === ub.origin &&
        this.normalizePathname(ua.pathname) === this.normalizePathname(ub.pathname)
      );
    } catch {
      return false;
    }
  }

  private getRuntimeCurrentSite(): Site {
    return {
      locale: Shopify.locale,
      region: Shopify.country || 'default',
      url: window.location.href,
      isCurrent: true,
    };
  }

  private getSitesFromCountryData(): Array<Site> {
    const countryData = getRegionSwitchCountryData();

    if (!countryData?.countries.length) {
      return [];
    }

    const currentCountryIsoCode = countryData.currentCountryIsoCode || Shopify.country;
    const currentLanguageIsoCode = countryData.currentLanguageIsoCode || Shopify.locale;

    return countryData.countries.flatMap((country) => {
      const locales = (
        country.availableLanguageIsoCodes.length > 0
          ? country.availableLanguageIsoCodes
          : [currentLanguageIsoCode]
      ).filter(
        (locale): locale is string => typeof locale === 'string' && locale.trim().length > 0,
      );

      return locales.map((locale) => ({
        locale: locale.trim(),
        region: country.isoCode,
        url: window.location.href,
        isCurrent: country.isoCode === currentCountryIsoCode && locale === currentLanguageIsoCode,
      }));
    });
  }

  private submitLocalizationForm(site: Site) {
    const wrapper = document.querySelector('[data-region-switch-localization-form]');
    const form = wrapper?.querySelector<HTMLFormElement>('form');
    const countryInput = form?.querySelector<HTMLInputElement>('[data-region-switch-country-code]');
    const languageInput = form?.querySelector<HTMLInputElement>(
      '[data-region-switch-language-code]',
    );

    if (!form || !countryInput || site.region === 'default') {
      return false;
    }

    countryInput.value = site.region;

    if (languageInput) {
      languageInput.value = site.locale;
    }

    form.requestSubmit();

    return true;
  }

  /**
   * Many alternates share the same canonical URL (e.g. root). Prefer Shopify locale+country,
   * then x-default, then first row so getCurrentSite matches the real market.
   */
  private resolveSiteFromLocation(): Site | undefined {
    const href = window.location.href;
    const candidates = this.sites.filter((site) => this.sameMarketUrl(site.url, href));
    if (candidates.length === 0) return undefined;
    if (candidates.length === 1) return candidates[0];

    const shopifyMatch = candidates.filter(
      (s) =>
        s.locale === Shopify.locale &&
        String(s.region).toUpperCase() === String(Shopify.country).toUpperCase(),
    );
    if (shopifyMatch.length === 1) return shopifyMatch[0];
    if (shopifyMatch.length > 1) return shopifyMatch[0];

    const defaultRegion = candidates.filter((s) => s.region === 'default');
    if (defaultRegion.length === 1) return defaultRegion[0];

    return candidates[0];
  }

  private loadDependecies() {
    return new Promise<void>((resolve, reject) => {
      window.Shopify.loadFeatures(
        [
          {
            name: 'consent-tracking-api',
            version: '0.1',
          },
        ],
        (error: unknown) => {
          if (error) {
            reject();
          } else {
            resolve();
          }
        },
      );
    });
  }

  private getSitesFromHreflangs() {
    const links = Array.from(
      document.querySelectorAll<HTMLLinkElement>('link[rel="alternate"][hreflang]'),
    );

    const current = new URL(window.location.href);

    type Row = {
      locale: string;
      region: string;
      url: string;
      samePath: boolean;
      shopifyMatch: boolean;
      hreflangRaw: string;
    };

    const rows: Row[] = links.map((link) => {
      let [locale, region] = link.hreflang.split('-');
      const hreflangRaw = link.hreflang;

      assertNonNullish(locale);

      if (locale === 'x') {
        locale = theme.primaryLocale || Shopify.locale;
      }

      locale = locale?.trim() || Shopify.locale;

      if (!region) {
        region = 'default';
      }

      let samePath = false;
      try {
        const linkUrl = new URL(link.href);
        samePath =
          linkUrl.origin === current.origin &&
          this.normalizePathname(linkUrl.pathname) === this.normalizePathname(current.pathname);
      } catch {
        samePath = false;
      }

      const regionMatchesCountry =
        String(region).toUpperCase() === String(Shopify.country).toUpperCase();
      const shopifyMatch = locale === Shopify.locale && regionMatchesCountry;

      return {
        locale,
        region: region ?? 'default',
        url: link.href,
        samePath,
        shopifyMatch,
        hreflangRaw,
      };
    });

    const samePathRows = rows.filter((r) => r.samePath);
    const shopifyRows = rows.filter((r) => r.shopifyMatch);

    let winnerIdx = -1;
    if (samePathRows.length === 1) {
      winnerIdx = rows.indexOf(NonNullish(samePathRows[0]));
    } else if (samePathRows.length > 1) {
      const onPathAndShopify = samePathRows.filter((r) => r.shopifyMatch);
      if (onPathAndShopify.length >= 1) {
        winnerIdx = rows.indexOf(NonNullish(onPathAndShopify[0]));
      } else {
        const xd = samePathRows.find((r) => r.hreflangRaw === 'x-default');
        winnerIdx = xd ? rows.indexOf(xd) : rows.indexOf(NonNullish(samePathRows[0]));
      }
    } else if (shopifyRows.length >= 1) {
      winnerIdx = rows.indexOf(NonNullish(shopifyRows[0]));
    }

    if (winnerIdx === -1) {
      if (samePathRows.length > 0) {
        winnerIdx = rows.indexOf(NonNullish(samePathRows[0]));
      } else if (shopifyRows.length > 0) {
        winnerIdx = rows.indexOf(NonNullish(shopifyRows[0]));
      }
    }

    return rows.map((row, i) => ({
      locale: row.locale,
      region: row.region,
      url: row.url,
      isCurrent: winnerIdx === i,
    }));
  }

  private normalizeRegionCode(region: string | undefined): string {
    if (!region?.trim() || region === 'default') {
      return 'default';
    }

    return region.trim().toUpperCase();
  }

  private getRegionFromStorage() {
    const region = localStorage.getItem(STORAGE_KEY)?.split('-').at(1)?.trim();

    return region ? this.normalizeRegionCode(region) : undefined;
  }

  private getLocaleFromStorage() {
    return localStorage.getItem(STORAGE_KEY)?.split('-').at(0)?.trim() || undefined;
  }

  private async getRegionFromShopify() {
    try {
      await this.loadDependecies();
    } catch {
      return undefined;
    }

    const region = Shopify.customerPrivacy?.getRegion()?.substring(0, 2)?.trim();

    return region ? this.normalizeRegionCode(region) : undefined;
  }

  private async getRegion() {
    return this.getRegionFromStorage() ?? (await this.getRegionFromShopify());
  }

  private getLocale() {
    return this.getLocaleFromStorage() || Shopify.locale?.trim() || 'en';
  }

  private saveSiteToStorage(site: Site, options: SaveSiteOptions = {}) {
    localStorage.setItem(STORAGE_KEY, `${site.locale}-${site.region}`);

    if (options.explicit) {
      localStorage.setItem(EXPLICIT_STORAGE_KEY, '1');
    }
  }

  hasSelectedSite() {
    return localStorage.getItem(STORAGE_KEY) !== null;
  }

  hasExplicitRegion() {
    return localStorage.getItem(EXPLICIT_STORAGE_KEY) === '1';
  }

  async getGeolocatedRegion() {
    return this.getRegionFromShopify();
  }

  keepCurrentSite() {
    this.saveSiteToStorage(this.getCurrentSite());
  }

  getRankedRegions({ region, locale }: RegionFilter) {
    return this.sites
      .sort((a, b) => Number(b.region === 'default') - Number(a.region === 'default'))
      .sort((a, b) => {
        if (locale) {
          return Number(b.locale === locale) - Number(a.locale === locale);
        }

        return 0;
      })
      .sort((a, b) => {
        if (region) {
          const normalized = this.normalizeRegionCode(region);
          return (
            Number(this.normalizeRegionCode(b.region) === normalized) -
            Number(this.normalizeRegionCode(a.region) === normalized)
          );
        }

        return 0;
      });
  }

  getFilteredRegions({ region, locale }: RegionFilter, shouldRank = false) {
    return (shouldRank ? this.getRankedRegions({ region, locale }) : this.sites).filter((site) => {
      if (region && this.normalizeRegionCode(site.region) !== this.normalizeRegionCode(region)) {
        return false;
      }

      if (locale && site.locale !== locale) {
        return false;
      }

      return true;
    });
  }

  getCurrentSite() {
    const fromLocation = this.resolveSiteFromLocation();
    const fromCurrentFlag = this.sites.find((site) => site.isCurrent);
    const found = fromLocation ?? fromCurrentFlag;

    return NonNullish(found);
  }

  isSameSite(a: Site, b: Site) {
    return (
      a.locale === b.locale &&
      this.normalizeRegionCode(a.region) === this.normalizeRegionCode(b.region)
    );
  }

  selectSite(site: Site, options: SelectSiteOptions = {}) {
    this.saveSiteToStorage(site, { explicit: options.explicit });

    if (this.sameMarketUrl(site.url, window.location.href)) {
      const current = this.getCurrentSite();

      if (site.region !== current.region || site.locale !== current.locale) {
        this.submitLocalizationForm(site);
      }

      return;
    }

    const url = new URL(site.url);
    url.search = window.location.search;
    url.hash = window.location.hash;
    url.searchParams.set('redirect', '1');

    if (options.explicit) {
      url.searchParams.set('region_selected', '1');
    }

    window.location.replace(url);
  }

  async doAutomaticRedirect() {
    const site = await this.getIntendedSite();

    this.selectSite(site);
  }

  async shouldRedirect() {
    if (!this.config.redirectIfHasSelected || !this.hasSelectedSite()) {
      return false;
    }

    const current = this.getCurrentSite();
    const intended = await this.getIntendedSite();

    return !this.sameMarketUrl(current.url, intended.url);
  }

  async shouldShowPrompt() {
    return !(await this.shouldRedirect());
  }

  async getIntendedSite() {
    const region = await this.getRegion();

    if (!region) {
      return this.getCurrentSite();
    }

    const locale = this.getLocale();
    const rankedWithLocale = this.getRankedRegions({ region, locale });
    const rankedMatch = rankedWithLocale.at(0) ?? this.getRankedRegions({ region }).at(0);

    return NonNullish(rankedMatch ?? this.getCurrentSite());
  }
}
